import {
    StreamRequestInfo,
    WidgetCustomManagedRestOptions,
    WidgetCustomStreamManager,
    WidgetRestByRequestUidConfig,
} from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import State from 'store/State';
import RequestProgressService from 'requestProgress/RequestProgressService';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import { callTradeOptimizer } from 'widgets/estimatedTradeCost/api/EstimatedTradeCostApi';
import { ActionsDispatcher } from 'store/ActionsDispatcher';
import IndigoApiError from 'communication/IndigoApiError';
import { WidgetRequestResponseByUidRestManager } from 'widgets/streaming/widgetStreamManager/RequestResponse/WidgetRequestResponseByUidRestManager';
import { DataServices } from 'store/Services';
import { WidgetId } from 'widgets/WidgetId';
import { selectPortfolioSelection } from 'portfolio/store/selectors';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';
import { selectWidgetInstanceConfig } from 'widgets/store/selectors';
import { EstimatedTradeCostConfigState } from 'widgets/estimatedTradeCost/widget/store/EstimatedTradeCostConfigState';

import {
    addError,
    addRequest,
    addResponse,
    clearData,
} from 'widgets/estimatedTradeCost/store/data/dataSlice';
import {
    toApiRequest,
    toRequest,
} from 'widgets/estimatedTradeCost/store/data/mapping';
import {
    EstimatedTradeCostRequest,
    toUid,
} from 'widgets/estimatedTradeCost/store/data/EstimatedTradeCostRequest';
import { EstimatedTradeCostApiResponse } from 'widgets/estimatedTradeCost/api/EstimatedTradeCostApiResponse';
import { selectWidgetComponentState } from 'widgets/store/component/selectors';
import { WidgetType } from 'widgets/WidgetType';
import { EstimatedTradeCostComponentState } from 'widgets/estimatedTradeCost/widget/store/componentSlice';

export const estimatedTradeCostStreamConfig: WidgetRestByRequestUidConfig<
    EstimatedTradeCostRequest,
    EstimatedTradeCostApiResponse
> = {
    apiUid: 'estimatedTradeCost',
    managerFactory(
        options: WidgetCustomManagedRestOptions
    ): WidgetCustomStreamManager {
        return new WidgetRequestResponseByUidRestManager(options);
    },
    getWidgetStreamRequestInfo(
        state: State,
        services: DataServices,
        id: WidgetId
    ): Promise<
        | StreamRequestInfo<EstimatedTradeCostRequest>[]
        | StreamRequestInfo<EstimatedTradeCostRequest>
        | undefined
    > {
        return Promise.resolve(
            getEstimatedTradeCostStreamRequestInfo(state, services, id)
        );
    },
    getStreamRequestInfosForSymbols(): Promise<
        StreamRequestInfo<EstimatedTradeCostRequest>[]
    > {
        return Promise.resolve([]);
    },
    callApi(
        dispatcher: ActionsDispatcher,
        requestProgressService: RequestProgressService,
        indigoServiceClient: IndigoServiceClient,
        _ds: DataServices,
        payload: EstimatedTradeCostRequest
    ): Promise<EstimatedTradeCostApiResponse> {
        const apiRequest = toApiRequest(payload);
        dispatcher.dispatch(addRequest({ request: payload, apiRequest }));
        return requestProgressService.invokeAndMonitorRequest(
            'callTradeOptimizer',
            (options) =>
                callTradeOptimizer(indigoServiceClient, apiRequest, options)
        );
    },
    clearRequestData(): void {
        return;
    },
    handleMessage(
        dispatcher: ActionsDispatcher,
        key: string,
        request: EstimatedTradeCostRequest,
        response: EstimatedTradeCostApiResponse
    ): void {
        dispatcher.dispatch(addResponse({ request, response }));
    },
    handleError(
        dispatcher: ActionsDispatcher,
        key: string,
        request: EstimatedTradeCostRequest,
        error: IndigoApiError
    ): void {
        dispatcher.dispatch(addError({ request, error }));
    },
    removeRequestData(dispatcher: ActionsDispatcher, key: string): void {
        dispatcher.dispatch(clearData(key));
    },
};

function getEstimatedTradeCostStreamRequestInfo(
    state: State,
    services: DataServices,
    widgetId: WidgetId
): StreamRequestInfo<EstimatedTradeCostRequest> | undefined {
    const selection = selectPortfolioSelection(state);
    const selectionWrapper = new PortfolioSelectionWrapper(selection);

    const order = selectionWrapper.selectedOrder;
    const config = selectWidgetInstanceConfig(
        state,
        widgetId
    ) as EstimatedTradeCostConfigState;
    const componentState =
        selectWidgetComponentState<EstimatedTradeCostComponentState>(
            state,
            WidgetType.EST_TRADE_COST_WIDGET,
            widgetId.widgetKey
        );

    const request = toRequest(order, config, componentState);

    if (!request || (!request.orderQtyRemaining && !request.specifiedQty)) {
        return undefined;
    }

    const uid = toUid(request);

    return {
        streamKey: uid,
        payloadUid: uid,
        payload: request,
    };
}
